import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchRequests, fetchCategories } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Input } from "@/components/ui/input";
import { Search, Clock, Coins, Users, Plus, FileText } from "lucide-react";
import SEO from "@/components/SEO";

const relTime = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function RequestsPage() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("open");

  useEffect(() => { fetchCategories().then(setCats).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      fetchRequests({ q: q || undefined, category: cat || undefined, status: status || undefined })
        .then(setItems).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat, status]);

  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.slug, c.name])), [cats]);

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <SEO title="Buyer Request Board" description="Browse live buyer briefs on Noxi. Freelancers — send custom offers; buyers — post briefs and get pitched by vetted sellers."/>
      <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
        <div>
          <div className="overline text-primary mb-2">◆ REQUEST BOARD</div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">Buyer briefs, live</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">Buyers post exactly what they need. Sellers pitch back with a price and delivery time.</p>
        </div>
        <button
          data-testid="post-request-btn"
          onClick={() => user ? navigate("/requests/new") : navigate("/login")}
          className="bg-primary text-primary-foreground px-6 py-3 mono text-xs hover:bg-primary/90 flex items-center gap-2"
        ><Plus className="w-4 h-4"/> POST A REQUEST</button>
      </div>

      {/* Filters */}
      <div className="grid md:grid-cols-[1fr_220px_180px] gap-3 mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50"/>
          <Input data-testid="requests-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search briefs" className="rounded-none border-foreground/20 pl-10"/>
        </div>
        <select data-testid="requests-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 px-3 border border-foreground/20 rounded-none bg-background">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <div className="flex gap-2">
          {["open", "fulfilled", ""].map((s) => (
            <button
              key={s || "all"}
              data-testid={`requests-status-${s || "all"}`}
              onClick={() => setStatus(s)}
              className={`mono text-xs flex-1 px-3 py-2 ${status === s ? "bg-foreground text-background" : "border border-foreground/30"}`}
            >{(s || "all").toUpperCase()}</button>
          ))}
        </div>
      </div>

      {/* Feed */}
      {items.length === 0 ? (
        <div className="border border-dashed border-foreground/20 p-12 text-center">
          <FileText className="w-8 h-8 mx-auto opacity-40 mb-3"/>
          <div className="text-lg font-semibold mb-1">No {status || "open"} requests yet</div>
          <p className="text-sm text-muted-foreground mb-4">Be the first to post — sellers are waiting.</p>
          <Link to="/requests/new" data-testid="empty-post-cta" className="inline-block bg-primary text-primary-foreground px-5 py-2 mono text-xs">POST A REQUEST</Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((r, i) => (
            <Link
              key={r.request_id}
              to={`/requests/${r.request_id}`}
              data-testid={`request-card-${i}`}
              className="block border border-foreground/15 bg-card p-5 hover:border-primary transition-colors"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="mono text-[10px] px-2 py-0.5 border border-foreground/30 uppercase">{catMap[r.category] || r.category}</span>
                    {r.status === "fulfilled" && <span className="mono text-[10px] px-2 py-0.5 border border-primary text-primary">FULFILLED</span>}
                    {r.status === "closed" && <span className="mono text-[10px] px-2 py-0.5 border border-foreground/40 opacity-70">CLOSED</span>}
                    <span className="mono text-[10px] opacity-60">{relTime(r.created_at)}</span>
                  </div>
                  <h3 className="text-lg font-semibold line-clamp-1">{r.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{r.description}</p>
                  <div className="flex items-center gap-2 mt-3">
                    {r.buyer_picture ? <img src={r.buyer_picture} alt="" className="w-6 h-6 rounded-full object-cover"/> : <div className="w-6 h-6 rounded-full bg-foreground/10 flex items-center justify-center text-xs">{r.buyer_name?.[0]}</div>}
                    <span className="text-xs text-muted-foreground">{r.buyer_name}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 whitespace-nowrap">
                  <div className="flex items-center gap-1.5 text-lg font-bold mono"><Coins className="w-4 h-4 text-primary"/> {format(r.budget, { decimals: 0 })}</div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mono"><Clock className="w-3.5 h-3.5"/> {r.delivery_days}d</div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mono"><Users className="w-3.5 h-3.5"/> {r.offers_count} offer{r.offers_count === 1 ? "" : "s"}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
